/**
 * Resolve merchant id + email for Stripe checkout / portal.
 * Sources: Redux userInfo, localStorage userInfo, userId, merchant_id, JWT access payload (user_id).
 */

export function readStoredUserInfo() {
	try {
		const raw = localStorage.getItem('userInfo')
		if (raw) return JSON.parse(raw)
	} catch {
		return null
	}
	return null
}

/** Best-effort: SimpleJWT access tokens include `user_id` in the payload. */
export function tryUserIdFromAccessToken(access) {
	if (!access || typeof access !== 'string') return ''
	const parts = access.split('.')
	if (parts.length !== 3) return ''
	try {
		const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
		const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
		const payload = JSON.parse(atob(padded))
		if (payload.user_id != null) return String(payload.user_id)
		if (payload.sub != null) return String(payload.sub)
	} catch {
		return ''
	}
	return ''
}

/**
 * @param {object|null|undefined} userInfo - state.userLogin.userInfo from Redux
 */
export function getMerchantCheckoutContext(userInfo) {
	const stored = readStoredUserInfo()
	const access =
		userInfo?.access ||
		stored?.access ||
		localStorage.getItem('authToken') ||
		localStorage.getItem('mockAuthToken') ||
		''

	const fromJwt = tryUserIdFromAccessToken(access)

	const uid =
		userInfo?.id ??
		userInfo?.uid ??
		stored?.id ??
		stored?.uid ??
		(localStorage.getItem('userId') ? localStorage.getItem('userId') : null) ??
		(fromJwt || null)

	const email = (
		userInfo?.email ||
		stored?.email ||
		localStorage.getItem('userEmail') ||
		''
	).trim()

	const merchantId =
		localStorage.getItem('merchant_id') ||
		localStorage.getItem('business_id') ||
		''

	const businessName =
		localStorage.getItem('business_name') ||
		stored?.username ||
		userInfo?.username ||
		''

	return { merchantId, merchantEmail: email, businessName, access }
}

/** Persist merchant display name for header/menu after onboarding or profile save. */
export function cacheMerchantBusinessName(name) {
	const trimmed = String(name || '').trim()
	if (!trimmed) return
	localStorage.setItem('business_name', trimmed)
}

/** Map `GET /user-businesses/` row to profile form state + cache label. */
export function businessRowToProfileFields(business) {
	if (!business) return null
	const business_name = String(business.business_name || business.businessName || '').trim()
	const row = {
		business_name,
		address: String(business.address || '').trim(),
		category: String(business.category || '').trim(),
		phone: String(business.phone || '').trim(),
		google_place_id: String(business.google_place_id || business.googlePlaceId || '').trim(),
		free_visit_for_review: Boolean(business.free_visit_for_review ?? business.freeVisitForReview),
	}
	if (business_name) cacheMerchantBusinessName(business_name)
	const merchantId = String(business.merchant_id || business.business_id || '').trim()
	if (merchantId) {
		localStorage.setItem('merchant_id', merchantId)
		localStorage.setItem('business_id', merchantId)
	}
	return row
}
